import type { BoardPoint, BoardSnapshot } from '@wx-weiqi/contracts';

import { BOARD_VIEW_SIZE, type BoardViewport } from './board-geometry';

export type HomeworkBoardCorner = 'TOP_LEFT' | 'TOP_RIGHT' | 'BOTTOM_LEFT' | 'BOTTOM_RIGHT';

export interface HomeworkBoardFocus {
  readonly corner: HomeworkBoardCorner;
  readonly viewport: BoardViewport;
}

const ZOOMED_VIEW_SIZE = 660;

export function boardViewportForCorner(corner: HomeworkBoardCorner): BoardViewport {
  const farEdge = BOARD_VIEW_SIZE - ZOOMED_VIEW_SIZE;
  return Object.freeze({
    x: corner === 'TOP_RIGHT' || corner === 'BOTTOM_RIGHT' ? farEdge : 0,
    y: corner === 'BOTTOM_LEFT' || corner === 'BOTTOM_RIGHT' ? farEdge : 0,
    width: ZOOMED_VIEW_SIZE,
    height: ZOOMED_VIEW_SIZE,
  });
}

export function homeworkBoardFocus(
  board: BoardSnapshot,
  additionalPoints: readonly BoardPoint[] = [],
): HomeworkBoardFocus | null {
  const occupied = board.stones.flatMap((stone, index) =>
    stone === null
      ? []
      : [
          {
            x: index % board.size,
            y: Math.floor(index / board.size),
          },
        ],
  );
  const points = [...occupied, ...additionalPoints];
  if (points.length === 0) return null;

  const low = Math.floor((board.size - 1) / 3);
  const high = board.size - 1 - low;
  const candidates: HomeworkBoardCorner[] = [];
  const every = (predicate: (point: (typeof occupied)[number]) => boolean): boolean =>
    points.every(predicate);

  if (every((point) => point.x <= high && point.y <= high)) candidates.push('TOP_LEFT');
  if (every((point) => point.x >= low && point.y <= high)) candidates.push('TOP_RIGHT');
  if (every((point) => point.x <= high && point.y >= low)) candidates.push('BOTTOM_LEFT');
  if (every((point) => point.x >= low && point.y >= low)) candidates.push('BOTTOM_RIGHT');
  if (candidates.length !== 1) return null;

  const corner = candidates[0];
  if (corner === undefined) return null;
  return {
    corner,
    viewport: boardViewportForCorner(corner),
  };
}
